import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, AlertCircle, Phone, MessageCircle, Facebook, Lightbulb, AlertTriangle, Lock, Check, X, HelpCircle } from 'lucide-react';

export default function WhatsAppGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 dark:bg-zinc-900">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-6 transition dark:text-zinc-50"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Kembali
        </button>

        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 dark:bg-zinc-900">
          <div className="flex items-center mb-4">
            <div className="bg-green-100 p-3 rounded-full mr-4 dark:bg-green-950/40">
              <MessageCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800 dark:text-zinc-50">
                Panduan Setup WhatsApp Business
              </h1>
              <p className="text-gray-600">Ikuti langkah mudah ini (5 menit)</p>
            </div>
          </div>
        </div>

        {/* Prerequisites */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6 mb-8 dark:bg-indigo-950/40">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center dark:text-zinc-50">
            <CheckCircle className="w-6 h-6 text-indigo-600 mr-2 dark:text-indigo-400" />
            Yang Anda Butuhkan
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 dark:bg-zinc-900">
              <Facebook className="w-8 h-8 text-indigo-600 mb-2 dark:text-indigo-400" />
              <h3 className="font-semibold mb-1">Akun Facebook</h3>
              <p className="text-sm text-gray-600">
                Untuk login dan otorisasi. Buat gratis di facebook.com jika belum punya.
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 dark:bg-zinc-900">
              <Phone className="w-8 h-8 text-green-600 mb-2 dark:text-green-400" />
              <h3 className="font-semibold mb-1">Nomor WhatsApp</h3>
              <p className="text-sm text-gray-600">
                Nomor bisnis Anda. Boleh yang sudah pakai WhatsApp biasa.
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 dark:bg-zinc-900">
              <MessageCircle className="w-8 h-8 text-orange-600 mb-2 dark:text-orange-400" />
              <h3 className="font-semibold mb-1">Akses SMS</h3>
              <p className="text-sm text-gray-600">
                Untuk menerima kode verifikasi via SMS.
              </p>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-6">
          {/* Step 1 */}
          <div className="bg-white rounded-xl shadow-lg p-6 dark:bg-zinc-900">
            <div className="flex items-start">
              <div className="bg-green-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold mr-4 flex-shrink-0">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800 mb-3 dark:text-zinc-50">
                  Klik "Hubungkan WhatsApp"
                </h3>
                <p className="text-gray-600 mb-4">
                  Di dashboard Nerava, masuk ke menu <strong>Channels</strong> atau halaman setup aiAgent, 
                  lalu klik tombol hijau <strong>"Hubungkan WhatsApp"</strong>.
                </p>
                <div className="bg-gray-100 rounded-lg p-4 dark:bg-zinc-800">
                  <p className="text-sm text-gray-700 dark:text-zinc-200">
                    <Lightbulb className="w-4 h-4 inline mr-1 text-blue-500" /><strong>Tips:</strong> Pastikan aiAgent Anda sudah dibuat sebelum menghubungkan WhatsApp.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="bg-white rounded-xl shadow-lg p-6 dark:bg-zinc-900">
            <div className="flex items-start">
              <div className="bg-green-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold mr-4 flex-shrink-0">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800 mb-3 dark:text-zinc-50">
                  Login dengan Facebook
                </h3>
                <p className="text-gray-600 mb-4">
                  Anda akan diarahkan ke halaman Facebook. Login dengan akun Facebook Anda:
                </p>
                <ul className="space-y-2 text-gray-700 mb-4 dark:text-zinc-200">
                  <li>• Masukkan email/nomor HP Facebook Anda</li>
                  <li>• Masukkan password</li>
                  <li>• Klik "Masuk"</li>
                </ul>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 dark:bg-yellow-950/40">
                  <p className="text-sm text-gray-700 dark:text-zinc-200">
                    <AlertTriangle className="w-4 h-4 inline mr-1 text-amber-500" /><strong>Belum punya Facebook?</strong> Buat akun gratis di <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline dark:text-indigo-400">facebook.com</a> dulu (5 menit).
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="bg-white rounded-xl shadow-lg p-6 dark:bg-zinc-900">
            <div className="flex items-start">
              <div className="bg-green-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold mr-4 flex-shrink-0">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800 mb-3 dark:text-zinc-50">
                  Klik "Allow" / "Izinkan"
                </h3>
                <p className="text-gray-600 mb-4">
                  Facebook akan menampilkan permission yang diminta Nerava:
                </p>
                <div className="bg-gray-50 rounded-lg p-4 mb-4 dark:bg-zinc-900">
                  <p className="font-semibold mb-2">Nerava meminta izin untuk:</p>
                  <ul className="space-y-1 text-sm text-gray-700 dark:text-zinc-200">
                    <li><Check className="w-3 h-3 inline mr-1 text-green-500" />Kelola WhatsApp Business Account</li>
                    <li><Check className="w-3 h-3 inline mr-1 text-green-500" />Kirim dan terima pesan WhatsApp</li>
                    <li><Check className="w-3 h-3 inline mr-1 text-green-500" />Akses riwayat pesan</li>
                  </ul>
                </div>
                <p className="text-gray-600 mb-4">
                  Klik tombol <strong className="text-green-600 dark:text-green-400">"Lanjutkan"</strong> atau <strong className="text-green-600 dark:text-green-400">"Allow"</strong>.
                </p>
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 dark:bg-indigo-950/40">
                  <p className="text-sm text-gray-700 dark:text-zinc-200">
                    <Lock className="w-4 h-4 inline mr-1 text-blue-500" /><strong>Keamanan:</strong> Nerava tidak menyimpan password Facebook Anda. 
                    Ini adalah proses OAuth standard yang aman.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="bg-white rounded-xl shadow-lg p-6 dark:bg-zinc-900">
            <div className="flex items-start">
              <div className="bg-green-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold mr-4 flex-shrink-0">
                4
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800 mb-3 dark:text-zinc-50">
                  Pilih atau Buat WhatsApp Business Account
                </h3>
                <p className="text-gray-600 mb-4">
                  Jika Anda <strong>sudah punya</strong> WhatsApp Business Account:
                </p>
                <ul className="space-y-2 text-gray-700 mb-4 dark:text-zinc-200">
                  <li>• Pilih WABA yang ingin Anda hubungkan</li>
                  <li>• Klik "Lanjutkan"</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  Jika <strong>belum punya</strong>:
                </p>
                <ul className="space-y-2 text-gray-700 mb-4 dark:text-zinc-200">
                  <li>• Meta akan otomatis membuat WABA baru untuk Anda</li>
                  <li>• Isi nama bisnis (contoh: "Toko Saya")</li>
                  <li>• Klik "Buat"</li>
                </ul>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 dark:bg-green-950/40">
                  <p className="text-sm text-gray-700 dark:text-zinc-200">
                    <CheckCircle className="w-4 h-4 inline mr-1 text-green-500" /><strong>Good News:</strong> Proses pembuatan WABA otomatis, tidak perlu submit dokumen atau tunggu approval!
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 5 */}
          <div className="bg-white rounded-xl shadow-lg p-6 dark:bg-zinc-900">
            <div className="flex items-start">
              <div className="bg-green-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold mr-4 flex-shrink-0">
                5
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800 mb-3 dark:text-zinc-50">
                  Input Nomor WhatsApp
                </h3>
                <p className="text-gray-600 mb-4">
                  Masukkan nomor WhatsApp bisnis Anda:
                </p>
                <div className="bg-gray-50 rounded-lg p-4 mb-4 dark:bg-zinc-900">
                  <p className="font-semibold mb-2">Format nomor:</p>
                  <p className="text-sm text-gray-700 mb-2 dark:text-zinc-200">Indonesia: <code className="bg-white px-2 py-1 rounded dark:bg-zinc-900">0812-3456-7890</code></p>
                  <p className="text-sm text-gray-700 dark:text-zinc-200">Atau: <code className="bg-white px-2 py-1 rounded dark:bg-zinc-900">+62 812-3456-7890</code></p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 dark:bg-yellow-950/40">
                  <p className="text-sm text-gray-700 mb-2 dark:text-zinc-200">
                    <AlertTriangle className="w-4 h-4 inline mr-1 text-amber-500" /><strong>Nomor yang bisa digunakan:</strong>
                  </p>
                  <ul className="text-sm text-gray-700 space-y-1 dark:text-zinc-200">
                    <li><Check className="w-3 h-3 inline mr-1 text-green-500" />Nomor yang belum pakai WhatsApp (recommended)</li>
                    <li><Check className="w-3 h-3 inline mr-1 text-green-500" />Nomor yang sudah pakai WhatsApp biasa</li>
                    <li><Check className="w-3 h-3 inline mr-1 text-green-500" />Nomor yang sudah pakai WhatsApp Business App</li>
                    <li><X className="w-3 h-3 inline mr-1 text-red-500" />Nomor yang sudah terhubung ke WABA lain</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Step 6 */}
          <div className="bg-white rounded-xl shadow-lg p-6 dark:bg-zinc-900">
            <div className="flex items-start">
              <div className="bg-green-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold mr-4 flex-shrink-0">
                6
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800 mb-3 dark:text-zinc-50">
                  Verifikasi via SMS
                </h3>
                <p className="text-gray-600 mb-4">
                  Meta akan mengirim kode verifikasi 6 digit ke nomor Anda via SMS:
                </p>
                <ul className="space-y-2 text-gray-700 mb-4 dark:text-zinc-200">
                  <li>• Tunggu SMS masuk (biasanya 10-30 detik)</li>
                  <li>• Input kode 6 digit</li>
                  <li>• Klik "Verifikasi"</li>
                </ul>
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 dark:bg-indigo-950/40">
                  <p className="text-sm text-gray-700 dark:text-zinc-200">
                    <Lightbulb className="w-4 h-4 inline mr-1 text-blue-500" /><strong>Tidak terima SMS?</strong> Pilih opsi "Kirim Ulang" atau verifikasi via "Phone Call".
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 7 */}
          <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-green-500 dark:bg-zinc-900">
            <div className="flex items-start">
              <div className="bg-green-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold mr-4 flex-shrink-0">
                <Check className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-green-600 mb-3 dark:text-green-400">
                  Selesai! AI Agent Aktif
                </h3>
                <p className="text-gray-600 mb-4">
                  Nomor WhatsApp Anda sekarang terhubung dengan Nerava dan siap membalas pesan otomatis!
                </p>
                <div className="bg-green-50 rounded-lg p-4 dark:bg-green-950/40">
                  <p className="font-semibold mb-2"><CheckCircle className="w-4 h-4 inline mr-1 text-green-500" />Yang sudah aktif:</p>
                  <ul className="space-y-1 text-sm text-gray-700 dark:text-zinc-200">
                    <li><Check className="w-3 h-3 inline mr-1 text-green-500" />Auto-reply 24/7</li>
                    <li><Check className="w-3 h-3 inline mr-1 text-green-500" />AI aiAgent dengan knowledge base</li>
                    <li><Check className="w-3 h-3 inline mr-1 text-green-500" />Analytics & statistik</li>
                    <li><Check className="w-3 h-3 inline mr-1 text-green-500" />Webhook terhubung</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="bg-white rounded-xl shadow-lg p-8 mt-8 dark:bg-zinc-900">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 dark:text-zinc-50">
            <HelpCircle className="w-6 h-6 inline mr-2" />Pertanyaan Umum (FAQ)
          </h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-800 mb-2 dark:text-zinc-50">
                Q: Apakah nomor WhatsApp saya harus baru?
              </h3>
              <p className="text-gray-600 text-sm">
                A: Tidak! Anda bisa pakai nomor yang sudah ada. Jika nomor sudah pakai WhatsApp biasa, 
                sistem akan upgrade ke WhatsApp Business API secara otomatis.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 mb-2 dark:text-zinc-50">
                Q: Apakah chat history saya akan hilang?
              </h3>
              <p className="text-gray-600 text-sm">
                A: Jika upgrade dari WhatsApp biasa/Business App, chat history tidak akan hilang. 
                Namun disarankan backup terlebih dahulu untuk jaga-jaga.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 mb-2 dark:text-zinc-50">
                Q: Berapa lama proses setup?
              </h3>
              <p className="text-gray-600 text-sm">
                A: Total waktu 3-5 menit jika semua persyaratan sudah siap (Facebook account + nomor WhatsApp).
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 mb-2 dark:text-zinc-50">
                Q: Apakah ada biaya dari Meta/WhatsApp?
              </h3>
              <p className="text-gray-600 text-sm">
                A: WhatsApp Business API gratis untuk 1.000 percakapan pertama per bulan. 
                Setelah itu ada biaya dari Meta (sekitar Rp 500/percakapan).
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 mb-2 dark:text-zinc-50">
                Q: Saya stuck/error saat setup, gimana?
              </h3>
              <p className="text-gray-600 text-sm">
                A: Hubungi customer support kami via chat di dashboard atau email ke support@nerava.id. 
                Kami siap bantu setup via video call jika diperlukan.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg p-8 mt-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">
            Siap Mulai Setup?
          </h2>
          <p className="mb-6">
            Ikuti langkah-langkah di atas dan aktifkan aiAgent WhatsApp Anda dalam 5 menit!
          </p>
          <button
            onClick={() => navigate('/whatsapp-onboarding')}
            className="bg-white text-green-600 font-semibold py-3 px-8 rounded-lg hover:bg-gray-100 transition dark:bg-zinc-900 dark:text-green-400"
          >
            Mulai Setup Sekarang
          </button>
        </div>
      </div>
    </div>
  );
}
